import React, { useState, useEffect, useRef } from "react";
import { X, Save, Calendar, Search, CheckCircle2, Loader2, Circle, AlertCircle } from "lucide-react";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";

/**
 * Get the Friday two weeks from today (uses local date to avoid timezone off-by-one)
 */
function getFridayTwoWeeksFromNow() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  while (d.getDay() !== 5) {
    d.setDate(d.getDate() + 1);
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Normalize date to YYYY-MM-DD
 */
function normalizeDate(dateValue) {
  if (!dateValue) return "";
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
}

/**
 * Normalize time to HH:mm
 */
function normalizeTime(timeValue) {
  if (!timeValue) return "";
  if (typeof timeValue === "string") {
    const match = timeValue.match(/^(\d{2}):(\d{2})/);
    if (match) return `${match[1]}:${match[2]}`;
  }
  return "";
}

function formatDateDisplay(dateString) {
  if (!dateString) return "Seleccionar fecha";
  if (typeof dateString === "string" && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split("-");
    return `${parseInt(day, 10)}/${parseInt(month, 10)}/${year}`;
  }
  const date = new Date(dateString);
  const d = date.getDate();
  const m = date.getMonth() + 1;
  const y = date.getFullYear();
  return `${d}/${m}/${y}`;
}

const ManifiestoInfoModal = ({
  mantisBusinessId,
  mantisBusinessUserId,
  token,
  preManifiesto,
  remesas,
  selectedRemesaIds,
  onClose,
  onSubmit,
  submitting,
  progress,
  isLogisticsDemo = false,
}) => {
  const selectedRemesas = remesas.filter((r) =>
    selectedRemesaIds.includes(r.remesa_id),
  );
  const firstRemesa = selectedRemesas[0];

  const sumFletes = selectedRemesas.reduce(
    (s, r) => s + (parseFloat(r.flete) || 0),
    0,
  );
  const sumPeso = selectedRemesas.reduce(
    (s, r) => s + (parseFloat(r.peso_mercancia_kg) || 0),
    0,
  );

  const uniqueConductores = [
    ...new Set(selectedRemesas.map((r) => r.cedula_conductor).filter(Boolean)),
  ];
  const conductoresConNombre = uniqueConductores.map((cedula) => {
    const remesa = selectedRemesas.find((r) => r.cedula_conductor === cedula);
    return { cedula, nombre: remesa?.nombre_conductor || "" };
  });
  const uniquePlacas = [
    ...new Set(selectedRemesas.map((r) => r.placa_vehiculo).filter(Boolean)),
  ];

  const [formData, setFormData] = useState({
    cedulaConductor: uniqueConductores.length === 1 ? uniqueConductores[0] : "",
    placa: uniquePlacas.length === 1 ? uniquePlacas[0] : "",
    placaRemolque: "",
    valorFlete: sumFletes,
    reteFuente: sumFletes * 0.01,
    reteIca: (sumFletes / 1000) * 4,
    anticipo: "",
    municipioOrigen: preManifiesto?.origen_id ?? "",
    municipioOrigenNombre: preManifiesto?.origen ?? "",
    municipioDestino: preManifiesto?.destino_id ?? "",
    municipioDestinoNombre: preManifiesto?.destino ?? "",
    nitCliente: firstRemesa?.nit_cliente ?? "",
    nitClienteRazonSocial: firstRemesa?.cliente ?? "",
    observaciones: "",
    fechaPagoSaldoManifiesto: getFridayTwoWeeksFromNow(),
    ruta: "",
    totalPeso: sumPeso,
    fechaLlegada: normalizeDate(firstRemesa?.fecha_cargue),
    horaLlegada: normalizeTime(firstRemesa?.hora_cargue),
    fechaEntrada: normalizeDate(firstRemesa?.fecha_cargue),
    horaEntrada: normalizeTime(firstRemesa?.hora_cargue),
    fechaSalida: normalizeDate(firstRemesa?.fecha_cargue),
    horaSalida: normalizeTime(firstRemesa?.hora_cargue),
  });

  const [origenSearchTerm, setOrigenSearchTerm] = useState(
    preManifiesto?.origen ?? "",
  );
  const [origenSearchResults, setOrigenSearchResults] = useState([]);
  const [showOrigenDropdown, setShowOrigenDropdown] = useState(false);
  const [searchingOrigen, setSearchingOrigen] = useState(false);

  const [destinoSearchTerm, setDestinoSearchTerm] = useState(
    preManifiesto?.destino ?? "",
  );
  const [destinoSearchResults, setDestinoSearchResults] = useState([]);
  const [showDestinoDropdown, setShowDestinoDropdown] = useState(false);
  const [searchingDestino, setSearchingDestino] = useState(false);

  const [nitSearchTerm, setNitSearchTerm] = useState(
    firstRemesa?.cliente || firstRemesa?.nit_cliente?.toString() || "",
  );
  const [nitSearchResults, setNitSearchResults] = useState([]);
  const [showNitDropdown, setShowNitDropdown] = useState(false);
  const [searchingNit, setSearchingNit] = useState(false);

  const fechaPagoRef = useRef(null);
  const fechaLlegadaRef = useRef(null);
  const fechaEntradaRef = useRef(null);
  const fechaSalidaRef = useRef(null);

  const origenSearchTimeoutRef = useRef(null);
  const destinoSearchTimeoutRef = useRef(null);
  const nitSearchTimeoutRef = useRef(null);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      reteFuente: (prev.valorFlete || 0) * 0.01,
      reteIca: ((prev.valorFlete || 0) / 1000) * 4,
    }));
  }, [formData.valorFlete]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".search-container")) {
        setShowOrigenDropdown(false);
        setShowDestinoDropdown(false);
        setShowNitDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      [
        origenSearchTimeoutRef,
        destinoSearchTimeoutRef,
        nitSearchTimeoutRef,
      ].forEach((ref) => {
        if (ref.current) clearTimeout(ref.current);
      });
    };
  }, []);

  const handleDateButtonClick = (ref) => {
    ref.current?.showPicker?.() || ref.current?.click();
  };

  const searchCiudades = async (searchTerm, type) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      if (type === "origen") {
        setOrigenSearchResults([]);
        setShowOrigenDropdown(false);
      } else {
        setDestinoSearchResults([]);
        setShowDestinoDropdown(false);
      }
      return;
    }
    if (type === "origen") setSearchingOrigen(true);
    else setSearchingDestino(true);
    try {
      const res = await roldanApi.searchCiudades(
        mantisBusinessId,
        token,
        searchTerm.trim(),
      );
      if (res?.success && res.data) {
        if (type === "origen") {
          setOrigenSearchResults(res.data);
          setShowOrigenDropdown(true);
        } else {
          setDestinoSearchResults(res.data);
          setShowDestinoDropdown(true);
        }
      } else {
        if (type === "origen") {
          setOrigenSearchResults([]);
          setShowOrigenDropdown(false);
        } else {
          setDestinoSearchResults([]);
          setShowDestinoDropdown(false);
        }
      }
    } catch (err) {
      console.error("Error searching ciudades:", err);
      if (type === "origen") {
        setOrigenSearchResults([]);
        setShowOrigenDropdown(false);
      } else {
        setDestinoSearchResults([]);
        setShowDestinoDropdown(false);
      }
    } finally {
      if (type === "origen") setSearchingOrigen(false);
      else setSearchingDestino(false);
    }
  };

  const searchTerceros = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setNitSearchResults([]);
      setShowNitDropdown(false);
      return;
    }
    setSearchingNit(true);
    try {
      const res = await roldanApi.searchTerceros(
        mantisBusinessId,
        token,
        searchTerm.trim(),
      );
      if (res?.success && res.data) {
        setNitSearchResults(res.data);
        setShowNitDropdown(true);
      } else {
        setNitSearchResults([]);
        setShowNitDropdown(false);
      }
    } catch (err) {
      console.error("Error searching terceros:", err);
      setNitSearchResults([]);
      setShowNitDropdown(false);
    } finally {
      setSearchingNit(false);
    }
  };

  const handleOrigenSearch = (value) => {
    setOrigenSearchTerm(value);
    if (origenSearchTimeoutRef.current)
      clearTimeout(origenSearchTimeoutRef.current);
    if (value.trim().length >= 2) {
      origenSearchTimeoutRef.current = setTimeout(
        () => searchCiudades(value, "origen"),
        300,
      );
    } else {
      setOrigenSearchResults([]);
      setShowOrigenDropdown(false);
    }
  };

  const handleDestinoSearch = (value) => {
    setDestinoSearchTerm(value);
    if (destinoSearchTimeoutRef.current)
      clearTimeout(destinoSearchTimeoutRef.current);
    if (value.trim().length >= 2) {
      destinoSearchTimeoutRef.current = setTimeout(
        () => searchCiudades(value, "destino"),
        300,
      );
    } else {
      setDestinoSearchResults([]);
      setShowDestinoDropdown(false);
    }
  };

  const handleNitSearch = (value) => {
    setNitSearchTerm(value);
    if (nitSearchTimeoutRef.current) clearTimeout(nitSearchTimeoutRef.current);
    if (value.trim().length >= 2) {
      nitSearchTimeoutRef.current = setTimeout(
        () => searchTerceros(value),
        300,
      );
    } else {
      setNitSearchResults([]);
      setShowNitDropdown(false);
    }
  };

  const selectOrigen = (ciudad) => {
    setFormData((prev) => ({
      ...prev,
      municipioOrigen: ciudad.CIUDAD,
      municipioOrigenNombre: ciudad.NOMBRE?.trim() ?? "",
    }));
    setOrigenSearchTerm(ciudad.NOMBRE?.trim() ?? "");
    setShowOrigenDropdown(false);
    setOrigenSearchResults([]);
  };

  const selectDestino = (ciudad) => {
    setFormData((prev) => ({
      ...prev,
      municipioDestino: ciudad.CIUDAD,
      municipioDestinoNombre: ciudad.NOMBRE?.trim() ?? "",
    }));
    setDestinoSearchTerm(ciudad.NOMBRE?.trim() ?? "");
    setShowDestinoDropdown(false);
    setDestinoSearchResults([]);
  };

  const selectNit = (tercero) => {
    setFormData((prev) => ({
      ...prev,
      nitCliente: tercero.NIT,
      nitClienteRazonSocial: tercero.RAZONSOCIAL ?? "",
    }));
    setNitSearchTerm(tercero.RAZONSOCIAL ?? String(tercero.NIT));
    setShowNitDropdown(false);
    setNitSearchResults([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const frontendInfo = {
      cedulaConductor: formData.cedulaConductor,
      placa: formData.placa,
      placaRemolque: (formData.placaRemolque || "").trim(),
      valorFlete: Number(formData.valorFlete) || 0,
      reteFuente: Number(formData.reteFuente) || 0,
      reteIca: Number(formData.reteIca) || 0,
      anticipo: Number(formData.anticipo) || 0,
      municipioOrigen: String(formData.municipioOrigen || ""),
      municipioDestino: String(formData.municipioDestino || ""),
      nitCliente: String(formData.nitCliente || ""),
      observaciones: formData.observaciones || "",
      fechaPagoSaldoManifiesto: formData.fechaPagoSaldoManifiesto || "",
      ruta: formData.ruta || "",
      totalPeso: Number(formData.totalPeso) || 0,
      fechaLlegada: formData.fechaLlegada || "",
      horaLlegada: formData.horaLlegada || "",
      fechaEntrada: formData.fechaEntrada || "",
      horaEntrada: formData.horaEntrada || "",
      fechaSalida: formData.fechaSalida || "",
      horaSalida: formData.horaSalida || "",
    };
    onSubmit(frontendInfo);
  };

  const showProgress = progress && progress.steps && progress.steps.length > 0;
  const totalSteps = progress?.steps?.length ?? 7;
  const completedCount = progress?.completedSteps?.length ?? 0;
  const progressPercent =
    totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
  const hasError = !!progress?.error;

  const canClose = !submitting || hasError;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => canClose && onClose()}
    >
      <div
        className="w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">
            {showProgress
              ? isLogisticsDemo
                ? "Generando manifiesto (demo — sin Ministerio)"
                : "Creando Manifiesto con RNDC"
              : "Información del Manifiesto"}
          </h2>
          <button
            onClick={onClose}
            disabled={submitting && !hasError}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={18} />
          </button>
        </div>

        {showProgress ? (
          <div className="p-6 overflow-y-auto flex-1 min-h-0">
            <div className="space-y-6">
              {isLogisticsDemo && (
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                  Modo demo: se muestran los pasos del proceso; los pasos RNDC
                  (Ministerio) no envían datos. El manifiesto y el PDF se
                  generan con datos internos/ERP.
                </div>
              )}
              {progress?.resumingFrom != null && (
                <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  <span>
                    Reanudando desde el paso {progress.resumingFrom} (pasos
                    anteriores ya completados).
                  </span>
                </div>
              )}
              <div>
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Progreso</span>
                  <span>
                    {completedCount} / {totalSteps} pasos
                  </span>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ease-out rounded-full ${
                      hasError ? "bg-red-500" : "bg-blue-600"
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              {hasError && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
                  <AlertCircle
                    size={20}
                    className="text-red-600 flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="font-semibold text-red-900">Error</p>
                    <p className="text-sm text-red-800 mt-1">
                      {progress.error}
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm font-semibold text-gray-700">
                  Pasos del proceso
                </p>
                <ul className="space-y-2">
                  {progress.steps.map((step, idx) => {
                    const stepId = typeof step === "object" ? step.id : idx;
                    const label =
                      typeof step === "object"
                        ? step.label || step.shortLabel
                        : `Paso ${idx + 1}`;
                    const isCompleted =
                      progress.completedSteps?.includes(stepId);
                    const isCurrent =
                      progress.currentStep === stepId && !hasError;
                    return (
                      <li
                        key={stepId}
                        className={`flex items-center gap-3 py-2 px-3 rounded-lg ${
                          isCurrent ? "bg-blue-50 border border-blue-200" : ""
                        } ${isCompleted && !isCurrent ? "bg-gray-50" : ""}`}
                      >
                        {isCompleted ? (
                          <CheckCircle2
                            size={20}
                            className="text-green-600 flex-shrink-0"
                          />
                        ) : isCurrent ? (
                          <Loader2
                            size={20}
                            className="text-blue-600 flex-shrink-0 animate-spin"
                          />
                        ) : (
                          <Circle
                            size={20}
                            className="text-gray-300 flex-shrink-0"
                          />
                        )}
                        <span
                          className={`text-sm ${
                            isCompleted
                              ? "text-gray-700"
                              : isCurrent
                                ? "text-blue-900 font-medium"
                                : "text-gray-500"
                          }`}
                        >
                          {label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {hasError && (
              <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-50"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="p-4 overflow-y-auto flex-1 min-h-0"
          >
            <div className="space-y-6">
              {isLogisticsDemo && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                  Demo logística: no se consultará tarifa RNDC ni se enviará
                  información al Ministerio.
                </div>
              )}
              {/* Conductor y Vehículo */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Conductor y Vehículo
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Cédula Conductor
                    </label>
                    {uniqueConductores.length > 1 ? (
                      <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2"
                        value={formData.cedulaConductor}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            cedulaConductor: e.target.value,
                          }))
                        }
                        required
                      >
                        <option value="">Seleccionar conductor</option>
                        {conductoresConNombre.map(({ cedula, nombre }) => (
                          <option key={cedula} value={cedula}>
                            {nombre ? `${cedula} - ${nombre}` : cedula}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2"
                        value={formData.cedulaConductor}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            cedulaConductor: e.target.value,
                          }))
                        }
                        placeholder="Cédula del conductor"
                        required
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Placa
                    </label>
                    {uniquePlacas.length > 1 ? (
                      <select
                        className="w-full border border-gray-200 rounded-lg px-3 py-2"
                        value={formData.placa}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, placa: e.target.value }))
                        }
                        required
                      >
                        <option value="">Seleccionar placa</option>
                        {uniquePlacas.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2"
                        value={formData.placa}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, placa: e.target.value }))
                        }
                        placeholder="Placa del vehículo"
                        required
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Placa Remolque (opcional)
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2"
                      value={formData.placaRemolque}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          placaRemolque: e.target.value,
                        }))
                      }
                      placeholder="Placa del remolque (ej: ABC123)"
                    />
                  </div>
                </div>
              </div>

              {/* Valores */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Valores
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Valor Flete
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2"
                      value={formData.valorFlete}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          valorFlete: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Retefuente (1%)
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2"
                      value={formData.reteFuente}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          reteFuente: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      ReteICA
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2"
                      value={formData.reteIca}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, reteIca: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Anticipo
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2"
                      value={formData.anticipo}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, anticipo: e.target.value }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Origen y Destino */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Origen y Destino
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="search-container">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Municipio Origen
                    </label>
                    <div className="relative">
                      <div className="flex items-center border border-gray-200 rounded-lg">
                        <Search size={16} className="text-gray-400 ml-3" />
                        <input
                          type="text"
                          className="w-full px-3 py-2 outline-none rounded-lg"
                          value={
                            origenSearchTerm || formData.municipioOrigenNombre
                          }
                          onChange={(e) => handleOrigenSearch(e.target.value)}
                          onFocus={() =>
                            origenSearchResults.length > 0 &&
                            setShowOrigenDropdown(true)
                          }
                          placeholder="Buscar ciudad de origen..."
                          required
                        />
                        {searchingOrigen && (
                          <div className="px-3">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                          </div>
                        )}
                      </div>
                      {showOrigenDropdown && origenSearchResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                          {origenSearchResults.map((ciudad, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => selectOrigen(ciudad)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">
                                {ciudad.NOMBRE?.trim()}
                              </div>
                              <div className="text-xs text-gray-500">
                                Código: {ciudad.CIUDAD}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="search-container">
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Municipio Destino
                    </label>
                    <div className="relative">
                      <div className="flex items-center border border-gray-200 rounded-lg">
                        <Search size={16} className="text-gray-400 ml-3" />
                        <input
                          type="text"
                          className="w-full px-3 py-2 outline-none rounded-lg"
                          value={
                            destinoSearchTerm || formData.municipioDestinoNombre
                          }
                          onChange={(e) => handleDestinoSearch(e.target.value)}
                          onFocus={() =>
                            destinoSearchResults.length > 0 &&
                            setShowDestinoDropdown(true)
                          }
                          placeholder="Buscar ciudad de destino..."
                          required
                        />
                        {searchingDestino && (
                          <div className="px-3">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                          </div>
                        )}
                      </div>
                      {showDestinoDropdown &&
                        destinoSearchResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                            {destinoSearchResults.map((ciudad, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => selectDestino(ciudad)}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900">
                                  {ciudad.NOMBRE?.trim()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Código: {ciudad.CIUDAD}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Cliente */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Cliente
                </h3>
                <div className="search-container">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    NIT Cliente
                  </label>
                  <div className="relative">
                    <div className="flex items-center border border-gray-200 rounded-lg">
                      <Search size={16} className="text-gray-400 ml-3" />
                      <input
                        type="text"
                        className="w-full px-3 py-2 outline-none rounded-lg"
                        value={
                          nitSearchTerm ||
                          formData.nitClienteRazonSocial ||
                          formData.nitCliente
                        }
                        onChange={(e) => handleNitSearch(e.target.value)}
                        onFocus={() =>
                          nitSearchResults.length > 0 &&
                          setShowNitDropdown(true)
                        }
                        placeholder="Buscar por razón social o NIT..."
                      />
                      {searchingNit && (
                        <div className="px-3">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                        </div>
                      )}
                    </div>
                    {showNitDropdown && nitSearchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {nitSearchResults.map((tercero, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => selectNit(tercero)}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">
                              {tercero.RAZONSOCIAL ?? ""}
                            </div>
                            <div className="text-xs text-gray-500">
                              NIT: {tercero.NIT}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Detalles */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Detalles
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Observaciones
                    </label>
                    <textarea
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 min-h-[70px]"
                      value={formData.observaciones}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          observaciones: e.target.value,
                        }))
                      }
                      placeholder="Observaciones..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Ruta
                    </label>
                    <textarea
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 min-h-[50px]"
                      value={formData.ruta}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, ruta: e.target.value }))
                      }
                      placeholder="Ruta..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Total Peso (kg)
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2"
                      value={formData.totalPeso}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          totalPeso: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Fecha Pago Saldo */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Fechas
                </h3>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Fecha Pago Saldo Manifiesto
                  </label>
                  <div className="relative">
                    <input
                      ref={fechaPagoRef}
                      type="date"
                      value={formData.fechaPagoSaldoManifiesto}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          fechaPagoSaldoManifiesto: e.target.value,
                        }))
                      }
                      className="absolute opacity-0 w-0 h-0 pointer-events-none"
                    />
                    <button
                      type="button"
                      onClick={() => handleDateButtonClick(fechaPagoRef)}
                      className={`w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between ${
                        formData.fechaPagoSaldoManifiesto
                          ? "border-gray-300 bg-white text-gray-900"
                          : "border-gray-300 bg-gray-50 text-gray-500"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Calendar size={16} className="text-gray-400" />
                        {formatDateDisplay(formData.fechaPagoSaldoManifiesto)}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Fechas y Horas Cargue */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Fechas y Horas de Cargue
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Fecha y Hora Llegada
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          ref={fechaLlegadaRef}
                          type="date"
                          value={formData.fechaLlegada}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              fechaLlegada: e.target.value,
                            }))
                          }
                          className="absolute opacity-0 w-0 h-0 pointer-events-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleDateButtonClick(fechaLlegadaRef)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm flex items-center gap-2"
                        >
                          <Calendar size={14} />
                          {formatDateDisplay(formData.fechaLlegada)}
                        </button>
                      </div>
                      <input
                        type="time"
                        className="w-24 border border-gray-200 rounded-lg px-2 py-2 text-sm"
                        value={formData.horaLlegada}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            horaLlegada: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Fecha y Hora Entrada
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          ref={fechaEntradaRef}
                          type="date"
                          value={formData.fechaEntrada}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              fechaEntrada: e.target.value,
                            }))
                          }
                          className="absolute opacity-0 w-0 h-0 pointer-events-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleDateButtonClick(fechaEntradaRef)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm flex items-center gap-2"
                        >
                          <Calendar size={14} />
                          {formatDateDisplay(formData.fechaEntrada)}
                        </button>
                      </div>
                      <input
                        type="time"
                        className="w-24 border border-gray-200 rounded-lg px-2 py-2 text-sm"
                        value={formData.horaEntrada}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            horaEntrada: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Fecha y Hora Salida
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input
                          ref={fechaSalidaRef}
                          type="date"
                          value={formData.fechaSalida}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              fechaSalida: e.target.value,
                            }))
                          }
                          className="absolute opacity-0 w-0 h-0 pointer-events-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleDateButtonClick(fechaSalidaRef)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm flex items-center gap-2"
                        >
                          <Calendar size={14} />
                          {formatDateDisplay(formData.fechaSalida)}
                        </button>
                      </div>
                      <input
                        type="time"
                        className="w-24 border border-gray-200 rounded-lg px-2 py-2 text-sm"
                        value={formData.horaSalida}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            horaSalida: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50"
                disabled={submitting}
              >
                <Save size={16} />
                {submitting ? "Generando..." : "Generar Manifiesto"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ManifiestoInfoModal;
